import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { loadContact } from 'data/contacts';
import InvestorProfile from 'app/InvestorOutreach/Profile';

import ContactProfile from './ContactProfile';

/**
 * Investor List row click (ATAI-180 follow-up) — opens the same Investor
 * Profile modal Search's "View Details" does (InvestorOutreach/Results/
 * InvestorCard.js), instead of the generic Contact Detail modal, for a
 * contact reached via `/contact/:groupId/:contactId` from an INVESTOR-type
 * group (see Contacts/index.js). The route only exists so the URL/back
 * button behave like the rest of Contacts — the modal itself is the same
 * popup-based `.open({ investor })` API used everywhere else.
 *
 * The grid row only carries the plain contact id, not the BatchData investor
 * id the profile needs, so this loads the contact first and reads
 * `batchdataInvestorId` off it (persisted on materialization, ATAI-116).
 *
 * `saved: true` is a placeholder, not a real lookup: a contact reachable from
 * the Investor List sidebar is definitionally already saved to *some*
 * investor list, but there's no per-investor "which lists" check yet — that
 * lands with ATAI-187 ("Already Saved" indicator). Swap this for the real
 * value once that ships.
 *
 * `contactId` (this route's own id, separate from the investor id above) is
 * what the modal's Investor Status / Touchpoint / Notes / Files sections
 * key on (S8 / ATAI-101) — see Profile/index.js's own header comment.
 */
const InvestorProfileRoute = ({ location, history }) => {
  const dispatch = useDispatch();
  const segments = location.pathname.split('/');
  const groupId = segments[2];
  const contactId = segments[3];
  const parentPath = `/contact/${groupId}`;

  const openedRef = useRef(false);
  const [notAnInvestor, setNotAnInvestor] = useState(false);

  useEffect(() => {
    // Guards against a stale response: if contactId changes (user clicks a
    // different row) before this loadContact call returns, its callback can
    // still fire late and must not act on data from a route we've already
    // navigated away from -- otherwise it can open/override the modal with
    // the wrong investor's data, or wrongly fall back to Contact Detail on
    // the new (perfectly valid) contact.
    let cancelled = false;
    openedRef.current = false;
    setNotAnInvestor(false);

    dispatch(
      loadContact(Number(contactId), ({ response }) => {
        if (cancelled) return;

        // loadContact's callback arg is `{ response }` where `response` is
        // itself the raw API body, which this endpoint wraps again as
        // `{ response: <contact> }` -- same double-unwrap ContactProfile.js
        // does (`response?.response`) a few lines below in this same tree.
        const investorId = response?.response?.batchdataInvestorId;

        if (!investorId) {
          // Not actually a materialized investor contact -- fall back to the
          // generic Contact Detail modal rather than show a broken profile.
          setNotAnInvestor(true);
          return;
        }

        openedRef.current = true;
        dispatch(
          InvestorProfile.open({
            investor: { id: investorId, contactId: Number(contactId), saved: true },
            onClose: () => history.push(parentPath),
          })
        );
      })
    );

    return () => {
      cancelled = true;
      if (openedRef.current) dispatch(InvestorProfile.close());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  if (notAnInvestor) return <ContactProfile />;
  return null;
};

export default withRouter(InvestorProfileRoute);
